// DESIGN-SPEC §5.3 Section 与 SectionHeading 区块标题
import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "@/lib/cn";
import { SpecimenLabel } from "./specimen-label";

export interface SectionProps extends HTMLAttributes<HTMLElement> {
  tight?: boolean;
  band?: boolean;
}

export function Section({
  tight = false,
  band = false,
  className,
  children,
  ...props
}: SectionProps) {
  return (
    <section
      {...props}
      className={cn(
        "content-section relative isolate overflow-clip",
        tight ? "px-5 py-10" : "px-5 py-14",
        band &&
          "border-y border-line/60 bg-forest-100/80 before:pointer-events-none before:absolute before:inset-0 before:-z-10 before:bg-[radial-gradient(circle_at_88%_12%,color-mix(in_srgb,var(--c-gilt)_13%,transparent),transparent_34%)] before:content-['']",
        className,
      )}
    >
      {children}
    </section>
  );
}

type HeadingTone = "default" | "tea" | "hirudin" | "herbal" | "inverse";

export interface SectionHeadingProps {
  eyebrow: {
    code: string;
    name: string;
    tone?: "default" | "tea" | "hirudin" | "herbal";
  };
  title: ReactNode;
  sub?: ReactNode;
  tone?: HeadingTone;
  className?: string;
}

const titleToneClasses: Record<HeadingTone, string> = {
  default: "text-ink",
  tea: "text-ink",
  hirudin: "text-ink",
  herbal: "text-ink",
  inverse: "text-paper-inverse",
};

const ruleToneClasses: Record<HeadingTone, string> = {
  default: "bg-forest-700",
  tea: "bg-tea",
  hirudin: "bg-hirudin",
  herbal: "bg-tea",
  inverse: "bg-paper-inverse",
};

export function SectionHeading({
  eyebrow,
  title,
  sub,
  tone = "default",
  className,
}: SectionHeadingProps) {
  return (
    <header className={className}>
      <SpecimenLabel
        code={eyebrow.code}
        name={eyebrow.name}
        variant={tone === "inverse" ? "inverse" : "eyebrow"}
        tone={eyebrow.tone ?? (tone === "inverse" ? "default" : tone)}
      />
      <h2
        className={cn(
          "mt-3 max-w-[15em] text-balance font-display text-[24px] leading-[1.42] font-semibold tracking-[0.01em]",
          titleToneClasses[tone],
        )}
      >
        {title}
      </h2>
      <span
        aria-hidden="true"
        className={cn("mt-5 block h-0.5 w-10", ruleToneClasses[tone])}
      />
      {sub ? (
        <p
          className={cn(
            "mt-3 max-w-[32em] text-[13px] leading-[1.75]",
            tone === "inverse" ? "text-paper-inverse/70" : "text-ink-3",
          )}
        >
          {sub}
        </p>
      ) : null}
    </header>
  );
}
